/**
 * Angka penyetelan renderer orb. Semuanya di sini supaya tidak tersebar di
 * kode (rule 15) — terutama ambang anti burn-in, yang perlu disetel sekali
 * jenis panel TV di NOC diketahui.
 */

import type { OrbSize } from "@/types/ai-assistant-orb";

/** Ukuran acuan saat admin menyetel kerapatan: sisi tombol bulat (FAB). */
export const ORB_REFERENCE_SIZE_PX = 56;

/**
 * Footprint tombol FAB — TETAP di semua ukuran. Ukuran orb hanya membesarkan
 * kanvas yang digambar (orb boleh meluber melewati tombol karena latar solid
 * sudah dihilangkan); tombol yang bisa diklik dan tata letak dashboard tidak
 * ikut berubah.
 */
export const FAB_FOOTPRINT_PX = 56;

/**
 * Sisi kanvas orb di FAB per ukuran — inilah yang membesar. Semua ≥
 * ORB_SMALL_SIZE_PX (24px), jadi varian partikel/jala di FAB tidak pernah jatuh
 * ke cincin. "Besar" dibatasi agar tidak menutupi kartu/badge tetangga.
 */
export const ORB_CANVAS_FAB_PX: Record<OrbSize, number> = {
	kecil: 56,
	sedang: 72,
	besar: 84,
};

/** Slot logo di header panel — TETAP, supaya baris header tidak bergeser. */
export const ORB_HEADER_SLOT_PX = 24;

/**
 * Sisi kanvas orb di header per ukuran; meluber dari slot tetap di atas tanpa
 * menggeser teks. "kecil" sengaja di bawah 24px → otomatis jadi cincin, sama
 * seperti header lama yang memang 22px.
 */
export const ORB_CANVAS_HEADER_PX: Record<OrbSize, number> = {
	kecil: 22,
	sedang: 28,
	besar: 34,
};

/**
 * Di bawah ambang ini, varian berpartikel/berjala tidak terbaca lagi dan
 * otomatis dirender sebagai cincin bercahaya.
 */
export const ORB_SMALL_SIZE_PX = 24;

/** Batas bawah jumlah titik supaya bola tidak terlihat bolong di ukuran mungil. */
export const ORB_MIN_POINTS = 120;

/** Orb berputar pelan; 30 fps tidak terbedakan dari 60 fps dan separuh bebannya. */
export const ORB_FPS_SMALL = 30;
/** Pratinjau besar di halaman admin boleh lebih mulus. */
export const ORB_FPS_LARGE = 60;
/** Di atas ukuran ini dianggap pratinjau besar. */
export const ORB_LARGE_SIZE_PX = 96;

// ── Anti burn-in (dirancang defensif seolah panel OLED) ────────────────────
// Jenis TV di NOC belum dikonfirmasi. Asumsi OLED dipilih karena asimetris:
// mubazir di LCD, tapi mencegah kerusakan permanen bila ternyata OLED.

/** Setelah sekian lama tanpa interaksi, orb mulai diredupkan. */
export const ORB_IDLE_DIM_AFTER_MS = 10 * 60 * 1000;
/** Setelah sekian lama tanpa interaksi, animasi dihentikan pada satu frame. */
export const ORB_IDLE_FREEZE_AFTER_MS = 30 * 60 * 1000;
/** Kecerahan orb saat idle penuh (1 = normal). */
export const ORB_IDLE_BRIGHTNESS = 0.35;
/** Lama transisi redup, supaya tidak terbaca sebagai layar mati. */
export const ORB_IDLE_FADE_MS = 8 * 1000;

/** Radius pergeseran halus posisi orb (pixel shifting), dalam piksel CSS. */
export const ORB_PIXEL_SHIFT_RADIUS_PX = 2.5;
/** Satu putaran penuh pergeseran halus. */
export const ORB_PIXEL_SHIFT_PERIOD_MS = 7 * 60 * 1000;

// ── Varian WebGL (liquid-glass, T5) ─────────────────────────────────────────

/**
 * z-index kanvas overlay WebGL bersama — di atas FAB (190) supaya sprite orb
 * tidak tertutup konten, di bawah modal Mantine (≈200) supaya dialog tetap di
 * atas. `pointer-events:none` pada kanvasnya sendiri membuat nilai ini aman
 * walau tumpang tindih dengan elemen lain.
 */
export const ORB_WEBGL_STAGE_Z_INDEX = 195;

// ── Varian liquid-morph (T6) ────────────────────────────────────────────────
/** Langkah raymarch per kelas ukuran (adaptif; dari prototipe putaran 2). */
export const ORB_MORPH_STEPS = { small: 36, medium: 44, large: 60 } as const;
/** Sisi sprite (px PERANGKAT) maks. kelas small / medium; di atasnya large. */
export const ORB_MORPH_SMALL_MAX_DEVICE_PX = 120;
export const ORB_MORPH_MEDIUM_MAX_DEVICE_PX = 220;
/**
 * Sprite liquid-morph = placeholder × skala ini (dipusatkan). Siluet menjangkau
 * 55% kanvas (tenang) sampai 82% (tetesan), terukur di prototipe; skala dibatasi
 * agar FAB "besar" (52px dari tepi layar) tak terpotong. Bola tenang ≈ 70% nominal.
 */
export const ORB_MORPH_SPRITE_SCALE = 1.3;
